import { useEffect, useId, useRef, useState } from 'react';
import { setlistsApi } from '../../../api/setlists';
import SetlistMediaLinks from '../../../components/feature/SetlistMediaLinks';
import type { Event } from '../../../types/event';
import type { SetlistDetail } from '../../../types/setlist';
import { formatSongKey } from '../../../utils/songKey';

interface Props {
  teamId: string;
  event: Event;
  canWrite: boolean;
  onClose: () => void;
}

export default function EventSetlistDialog({ teamId, event, canWrite, onClose }: Props) {
  const [setlist, setSetlist] = useState<SetlistDetail | null>(null);
  const [error, setError] = useState('');
  const [editingLinksId, setEditingLinksId] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    return () => previousFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    let active = true;
    if (!event.setlist_id) return;

    setlistsApi.get(teamId, event.setlist_id)
      .then((response) => {
        if (active) setSetlist(response.data);
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar o repertório.');
      });

    return () => {
      active = false;
    };
  }, [event.setlist_id, teamId]);

  const handleSaveLinks = async (item: SetlistDetail['items'][number]) => {
    if (!setlist) return;
    setError('');
    try {
      const response = await setlistsApi.updateItem(teamId, setlist.id, item.id, {
        youtube_url: youtubeUrl.trim(),
        spotify_url: spotifyUrl.trim(),
      });
      setSetlist((current) => current ? {
        ...current,
        items: current.items.map((currentItem) => currentItem.id === item.id ? response.data : currentItem),
      } : current);
      setEditingLinksId(null);
    } catch {
      setError('Não foi possível salvar os links.');
    }
  };

  const handleKeyDown = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => {
    if (keyboardEvent.key === 'Escape') {
      onClose();
      return;
    }
    if (keyboardEvent.key !== 'Tab') return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled])') ?? [],
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (keyboardEvent.shiftKey && document.activeElement === first) {
      keyboardEvent.preventDefault();
      last.focus();
    } else if (!keyboardEvent.shiftKey && document.activeElement === last) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111118] p-6"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-white/30">{event.name}</p>
            <h2 id={titleId} className="text-lg font-semibold text-white">
              {setlist?.name ?? event.setlist_name ?? 'Repertório'}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Fechar repertório"
            onClick={onClose}
            className="text-white/40 hover:text-white"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {error && <p className="py-8 text-center text-sm text-red-400">{error}</p>}
        {!error && !setlist && <p className="py-8 text-center text-sm text-white/30">Carregando repertório...</p>}
        {setlist && setlist.items.length === 0 && (
          <p className="py-8 text-center text-sm text-white/30">Nenhuma música neste setlist.</p>
        )}
        {setlist && setlist.items.length > 0 && (
          <ol className="max-h-[60vh] space-y-2 overflow-y-auto">
            {setlist.items.map((item, index) => (
              <li key={item.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <span className="text-xs text-white/20">{index + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{item.song_title}</p>
                  <div className="flex gap-1.5 text-xs text-white/35">
                    <span>{item.song_artist || 'Sem artista'}</span>
                    {(item.key_override || item.song_key) && (
                      <span>Tom: {formatSongKey(item.key_override || item.song_key, item.song_mode)}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <SetlistMediaLinks
                    songTitle={item.song_title}
                    youtubeUrl={item.youtube_url}
                    spotifyUrl={item.spotify_url}
                  />
                  {canWrite && editingLinksId !== item.id && (
                    <button
                      type="button"
                      aria-label={`Editar links de ${item.song_title}`}
                      onClick={() => {
                        setEditingLinksId(item.id);
                        setYoutubeUrl(item.youtube_url);
                        setSpotifyUrl(item.spotify_url);
                      }}
                      className="text-xs text-white/50 hover:text-white whitespace-nowrap"
                    >
                      Editar links
                    </button>
                  )}
                </div>
                {canWrite && editingLinksId === item.id && (
                  <div className="mt-3 grid gap-2">
                    <label className="text-xs text-white/50">
                      YouTube
                      <input
                        type="url"
                        aria-label={`URL do YouTube para ${item.song_title}`}
                        value={youtubeUrl}
                        onChange={(inputEvent) => setYoutubeUrl(inputEvent.target.value)}
                        className="mt-1 w-full rounded border border-white/10 bg-white/5 px-2 py-1.5 text-white outline-none focus:border-[#C8A96E]/50"
                      />
                    </label>
                    <label className="text-xs text-white/50">
                      Spotify
                      <input
                        type="url"
                        aria-label={`URL do Spotify para ${item.song_title}`}
                        value={spotifyUrl}
                        onChange={(inputEvent) => setSpotifyUrl(inputEvent.target.value)}
                        className="mt-1 w-full rounded border border-white/10 bg-white/5 px-2 py-1.5 text-white outline-none focus:border-[#C8A96E]/50"
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setEditingLinksId(null)} className="text-xs text-white/50 hover:text-white">
                        Cancelar
                      </button>
                      <button type="button" onClick={() => handleSaveLinks(item)} className="text-xs text-[#C8A96E] hover:text-[#D4B87A]">
                        Salvar links
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
